/**
 * Keeping the address bar and the context bar from contradicting each other.
 *
 * If the URL says `/products/A` while the selector says product B, one of them
 * is lying to the operator — and whichever they believe, the other is what gets
 * bookmarked, shared or reloaded. So a product switch made while standing on a
 * product-scoped route moves the route with it, staying on the same kind of page
 * rather than dumping the operator back to a list.
 */

/** The product-scoped tabs, as route suffixes. */
const PRODUCT_TABS = ['environments', 'plans'] as const;

/**
 * Where to go when the product changes, or `null` to stay put.
 *
 * `null` is the answer for organization-level pages — Overview, Products, Audit
 * Log. Changing context there is not a request to go somewhere, and teleporting
 * the operator into a detail page they did not ask for is its own defect.
 */
export function productRouteAfterSwitch(pathname: string, nextProductId: string): string | null {
  const match = /^\/products\/([^/]+)(?:\/([^/]+))?\/?$/.exec(pathname);
  if (match === null) return null;

  const [, currentProductId, tab] = match;
  if (currentProductId === nextProductId) return null;

  // Stay on the same tab where the destination has one, so switching product
  // while comparing environments keeps comparing environments.
  if (tab !== undefined && (PRODUCT_TABS as readonly string[]).includes(tab)) {
    return `/products/${nextProductId}/${tab}`;
  }
  return `/products/${nextProductId}`;
}

/**
 * Whether the current route is scoped to a product that the new organization
 * cannot own.
 *
 * On an organization switch the product underneath is gone by definition, so a
 * product-scoped URL is stale the moment the switch happens. The operator goes
 * to a neutral organization-level page instead — deliberately NOT to "the same
 * slug in the new organization", because an identical name is not the same
 * resource and quietly substituting one for the other is how somebody edits the
 * wrong tenant's product.
 */
export function isProductScopedRoute(pathname: string): boolean {
  return /^\/products\/[^/]+/.test(pathname);
}

export const NEUTRAL_ORGANIZATION_ROUTE = '/products';
